import { retiredTodayDestination } from "@/lib/areas/legacy-today";

type Redirect = {
  source: string;
  destination: string;
  statusCode: 301 | 308;
};

export const REDIRECTS: Redirect[] = [
  // Homepage duplicate: Next.js also serves the root page at /index, which
  // used to be the homepage canonical. Only the exact path matches, so RSC and
  // segment requests (/index.rsc, /index.segments/...) are untouched.
  { source: "/index", destination: "/", statusCode: 301 },

  // === Existing redirects (carried forward from next.config.ts) ===
  { source: "/festival-weather", destination: "/festivals/weather", statusCode: 301 },
  { source: "/festival-weather/:path*", destination: "/festivals/:path*", statusCode: 301 },
  { source: "/festivals/reading", destination: "/festivals/reading-leeds", statusCode: 301 },
  { source: "/festivals/leeds", destination: "/festivals/reading-leeds", statusCode: 301 },
  { source: "/festivals/splendour-in-the-grass", destination: "/festivals/splendour", statusCode: 301 },
  { source: "/festivals/rhythm-and-vines", destination: "/festivals/rhythm-vines", statusCode: 301 },
  { source: "/best-skiing-today", destination: "/conditions/piste", statusCode: 301 },
  { source: "/best-surf-today", destination: "/conditions/surf", statusCode: 301 },
  { source: "/research/best-time-to-go", destination: "/best-time-to-go/ski", statusCode: 301 },
  { source: "/leaderboards", destination: "/research", statusCode: 301 },
  { source: "/leaderboards/quality", destination: "/research/directory", statusCode: 301 },
  { source: "/leaderboards/value", destination: "/research/directory", statusCode: 301 },
  { source: "/leaderboards/quietest", destination: "/research/school-holidays", statusCode: 301 },
  { source: "/leaderboards/reliability", destination: "/research", statusCode: 301 },
  // /best-this-week and /best-this-week/:sport are answered by
  // resolveRetiredTodayRedirect below (they used to chain through /today).
  // Explore hub retirement (carried forward, updated destinations)
  { source: "/explore", destination: "/conditions", statusCode: 301 },
  { source: "/explore/winter", destination: "/conditions", statusCode: 301 },
  { source: "/explore/summer", destination: "/conditions", statusCode: 301 },
  { source: "/explore/festivals", destination: "/festivals", statusCode: 301 },
  { source: "/explore/winter/:slug", destination: "/guides/:slug", statusCode: 301 },
  { source: "/explore/summer/:slug", destination: "/guides/:slug", statusCode: 301 },
  { source: "/explore/festivals/:slug", destination: "/festivals/:slug", statusCode: 301 },
  { source: "/explore/guides/:slug", destination: "/guides/:slug", statusCode: 301 },
  { source: "/explore/tools/quiz", destination: "/plan/quiz", statusCode: 301 },
  { source: "/explore/tools/luck-factor", destination: "/conditions/piste", statusCode: 301 },
  { source: "/explore/tools/festival-weather", destination: "/festivals/weather", statusCode: 301 },
  { source: "/explore/tools/get-hired-meter", destination: "/conditions", statusCode: 301 },
  { source: "/tools/get-hired-meter", destination: "/conditions", statusCode: 301 },
  { source: "/explore/tools/festival-quiz", destination: "/festivals/quiz", statusCode: 301 },
  { source: "/explore/submit", destination: "/submit", statusCode: 301 },
  // 308 rewrite (carried forward). The sport lens rides in a fragment so the
  // redirect lands on the one canonical spot URL.
  { source: "/ski/conditions/piste/:slug", destination: "/conditions/:slug#sport=piste", statusCode: 308 },

  // === New migration redirects ===

  // Old hub pages -> Conditions
  { source: "/ski", destination: "/conditions", statusCode: 301 },
  { source: "/summer", destination: "/conditions", statusCode: 301 },
  { source: "/tools", destination: "/conditions", statusCode: 301 },

  // Conditions: ski/conditions/* -> /conditions/*
  { source: "/ski/conditions", destination: "/conditions/piste", statusCode: 301 },
  { source: "/ski/conditions/piste", destination: "/conditions/piste", statusCode: 301 },
  { source: "/ski/conditions/off-piste", destination: "/conditions/off-piste", statusCode: 301 },
  { source: "/ski/conditions/touring", destination: "/conditions/touring", statusCode: 301 },
  { source: "/ski/conditions/:slug", destination: "/conditions/:slug", statusCode: 301 },
  { source: "/ski/best-today", destination: "/conditions/piste", statusCode: 301 },

  // Conditions: foil discipline aliases -> canonical sport slugs
  { source: "/conditions/prone-foil", destination: "/conditions/foil", statusCode: 301 },

  // Conditions: summer/conditions/* -> /conditions/*
  { source: "/summer/conditions", destination: "/conditions/surf", statusCode: 301 },
  { source: "/summer/conditions/surf", destination: "/conditions/surf", statusCode: 301 },
  { source: "/summer/conditions/wind", destination: "/conditions/wind", statusCode: 301 },
  { source: "/summer/conditions/foil", destination: "/conditions/foil", statusCode: 301 },
  { source: "/summer/conditions/diving", destination: "/conditions/diving", statusCode: 301 },
  { source: "/summer/conditions/open-water", destination: "/conditions/open-water", statusCode: 301 },
  { source: "/summer/conditions/sup", destination: "/conditions/sup", statusCode: 301 },
  { source: "/summer/conditions/sailing", destination: "/conditions/sailing", statusCode: 301 },
  { source: "/summer/conditions/mtb", destination: "/conditions/mtb", statusCode: 301 },
  { source: "/summer/conditions/water-quality", destination: "/conditions/water-quality", statusCode: 301 },
  { source: "/summer/conditions/compare", destination: "/conditions/compare", statusCode: 301 },
  { source: "/summer/conditions/:slug", destination: "/conditions/:slug", statusCode: 301 },
  { source: "/summer/best-today", destination: "/conditions/surf", statusCode: 301 },
  { source: "/summer/wind-sports/best-today", destination: "/conditions/wind", statusCode: 301 },

  // Research: ski/leaderboards/* -> /research/*
  { source: "/ski/leaderboards", destination: "/research", statusCode: 301 },
  { source: "/ski/leaderboards/quality", destination: "/research/directory", statusCode: 301 },
  { source: "/ski/leaderboards/value", destination: "/research/directory", statusCode: 301 },
  { source: "/ski/leaderboards/quietest", destination: "/research/school-holidays", statusCode: 301 },
  { source: "/research/quietest", destination: "/research/school-holidays", statusCode: 301 },
  { source: "/ski/leaderboards/reliability", destination: "/research", statusCode: 301 },
  { source: "/research/reliability", destination: "/research", statusCode: 301 },
  { source: "/ski/best-time-to-go", destination: "/best-time-to-go/ski", statusCode: 301 },
  { source: "/research/quality", destination: "/research/directory", statusCode: 301 },
  { source: "/research/value", destination: "/research/directory", statusCode: 301 },
  { source: "/awards", destination: "/research/awards", statusCode: 301 },
  { source: "/awards/:sport", destination: "/research/awards/:sport", statusCode: 301 },

  // Plan Your Season
  { source: "/quiz", destination: "/plan/quiz", statusCode: 301 },
  { source: "/seasons", destination: "/plan/calendar", statusCode: 301 },
  { source: "/seasons/calendar", destination: "/plan/calendar", statusCode: 301 },
  { source: "/ski/season", destination: "/conditions/season/ski", statusCode: 301 },
  { source: "/summer/season/:sport", destination: "/conditions/season/:sport", statusCode: 301 },
  { source: "/plan/season/ski", destination: "/conditions/season/ski", statusCode: 301 },
  { source: "/plan/season/:sport", destination: "/conditions/season/:sport", statusCode: 301 },
  { source: "/courses", destination: "/plan/courses", statusCode: 301 },
  { source: "/courses/:path*", destination: "/plan/courses/:path*", statusCode: 301 },

  // Community
  { source: "/crew", destination: "/community/crew", statusCode: 301 },
  { source: "/crew/:slug", destination: "/community/crew/:slug", statusCode: 301 },
  { source: "/crew/:slug/chat", destination: "/community/crew/:slug/chat", statusCode: 301 },
  { source: "/forum", destination: "/community/forum", statusCode: 301 },
  { source: "/forum/:path*", destination: "/community/forum/:path*", statusCode: 301 },
  { source: "/creators", destination: "/community/creators", statusCode: 301 },

  // Companion
  { source: "/my-ski", destination: "/companion", statusCode: 308 },
  { source: "/my-surf", destination: "/companion", statusCode: 308 },
  { source: "/my-surf/wrapped", destination: "/companion/wrapped", statusCode: 308 },
  { source: "/calls", destination: "/companion/calls", statusCode: 308 },
  { source: "/briefings", destination: "/companion/briefings", statusCode: 308 },
  { source: "/briefings/:id", destination: "/companion/briefings/:id", statusCode: 308 },

  // Retired candidate dashboard
  { source: "/dashboard", destination: "/companion", statusCode: 301 },
  { source: "/dashboard/profile", destination: "/profile", statusCode: 301 },
  { source: "/dashboard/messages", destination: "/inbox", statusCode: 301 },
  { source: "/dashboard/crews", destination: "/community/crew", statusCode: 301 },

  // Legacy /conditions -> now real page exists, remove old redirect
  // NOTE: Do NOT redirect /conditions since it will have a real page.tsx
  // The old { source: "/conditions", destination: "/ski/conditions/piste" } is intentionally removed.
];

// Legacy per-sport spot URLs. Each sport now shares one canonical spot page,
// /conditions/<slug>, and the sport lens travels in a #sport= fragment, which
// crawlers ignore and the conditions layout applies after hydration.
export const LEGACY_SPORT_SPOT_REDIRECTS: readonly Readonly<{
  segment: string;
  sportKey: string;
}>[] = [
  { segment: "wind", sportKey: "wind" },
  { segment: "foil", sportKey: "foil" },
  { segment: "downwind-foil", sportKey: "downwind-foil" },
  { segment: "wing-foil", sportKey: "wing-foil" },
  { segment: "diving", sportKey: "diving" },
  { segment: "open-water", sportKey: "swimming" },
  { segment: "mtb", sportKey: "mtb" },
  { segment: "bodyboarding", sportKey: "bodyboarding" },
  // Paddle spots open on the Touring profile, matching the retired page.
  { segment: "sup", sportKey: "sup-touring" },
];

// Sailing venues live at /conditions/sailing/<slug>. The bare
// /conditions/<slug> form was emitted by the old sitemap and board links, so
// middleware sends it to the canonical page in one permanent hop. Kept in sync
// with SAILING_LOCATIONS by lib/__tests__/redirects.test.ts; a venue added only
// in the database still gets a permanent redirect from the spot page itself.
export const SAILING_SPOT_SLUGS: readonly string[] = [
  "rutland-water", "grafham-water", "carsington-water", "welsh-harp",
  "pitsford-water", "llandegfedd", "chichester-harbour", "camel-estuary",
  "exe-estuary", "hamble-river", "menai-strait", "burnham-on-crouch",
  "hayling-island", "weymouth-portland", "whitstable", "pwllheli", "largs",
  "dun-laoghaire", "fornells-bay", "palma-mallorca", "lake-garda", "hyeres",
  "la-rochelle", "kiel", "medemblik", "aarhus", "annapolis", "newport-ri",
  "biscayne-bay", "san-francisco-bay", "columbia-gorge", "kingston-ontario",
  "sydney-harbour", "belmont-lake-macquarie", "sandringham-melbourne",
  "auckland-waitemata", "table-bay-cape-town", "vaal-dam",
];

// Festival job guides are standalone pages under /festivals/<slug>. Their old
// /guides/<slug> URLs were answered by a streamed page, which sent a 200 with a
// meta refresh. Middleware now issues a real 301. Kept in sync with the
// festival entries in lib/guides by lib/__tests__/redirects.test.ts.
export const FESTIVAL_GUIDE_SLUGS: readonly string[] = [
  "festival-jobs-uk",
  "festival-bar-staff-jobs-uk",
  "festival-catering-jobs",
  "glastonbury-jobs",
  "boomtown-jobs",
  "uk-festivals",
];

// The pre-2026 /summer/conditions/<sport>/<slug> spot pages. They go straight
// to the canonical spot page rather than through /conditions/<sport>/<slug>.
const LEGACY_SUMMER_SPORT_SEGMENTS: readonly string[] = [
  "wind", "foil", "diving", "open-water", "mtb", "sup",
];

const CANONICAL_SPOT_REDIRECTS: Redirect[] = [
  ...LEGACY_SPORT_SPOT_REDIRECTS.map(({ segment, sportKey }): Redirect => ({
    source: `/conditions/${segment}/:slug`,
    destination: `/conditions/:slug#sport=${sportKey}`,
    statusCode: 301,
  })),
  ...LEGACY_SPORT_SPOT_REDIRECTS
    .filter(({ segment }) => LEGACY_SUMMER_SPORT_SEGMENTS.includes(segment))
    .map(({ segment, sportKey }): Redirect => ({
      source: `/summer/conditions/${segment}/:slug`,
      destination: `/conditions/:slug#sport=${sportKey}`,
      statusCode: 301,
    })),
  { source: "/summer/conditions/sailing/:slug", destination: "/conditions/sailing/:slug", statusCode: 301 },
  ...SAILING_SPOT_SLUGS.map((slug): Redirect => ({
    source: `/conditions/${slug}`,
    destination: `/conditions/sailing/${slug}`,
    statusCode: 301,
  })),
  ...FESTIVAL_GUIDE_SLUGS.map((slug): Redirect => ({
    source: `/guides/${slug}`,
    destination: `/festivals/${slug}`,
    statusCode: 301,
  })),
];

// Topic consolidation (SEO plan P1-3, 28 Sep 2026). Two to four URLs were
// competing for each of these topics, so each topic now has one main page and
// the rest 301 to it. These come first in ALL_REDIRECTS so older aliases (for
// example /explore/festivals/<slug>) reach the survivor in a single hop.
export const FESTIVAL_JOBS_PATH = "/festivals/festival-jobs-uk";

export const CONSOLIDATION_REDIRECTS: Redirect[] = [
  // Festival jobs: one evergreen page instead of a year-locked URL.
  { source: "/festivals/festival-jobs-uk-2026", destination: FESTIVAL_JOBS_PATH, statusCode: 301 },
  { source: "/guides/festival-jobs-uk-2026", destination: FESTIVAL_JOBS_PATH, statusCode: 301 },
  { source: "/guides/festival-jobs-uk", destination: FESTIVAL_JOBS_PATH, statusCode: 301 },
  { source: "/explore/festivals/festival-jobs-uk-2026", destination: FESTIVAL_JOBS_PATH, statusCode: 301 },
  { source: "/blog/festival-jobs-uk", destination: FESTIVAL_JOBS_PATH, statusCode: 301 },
  // Chalet host: three URLs to one.
  { source: "/blog/how-to-become-a-chalet-host", destination: "/guides/chalet-host-jobs", statusCode: 301 },
  { source: "/blog/chalet-chef-vs-chalet-host", destination: "/guides/chalet-host-jobs", statusCode: 301 },
  // Yacht crew with no experience.
  { source: "/blog/getting-into-yachting-no-experience", destination: "/guides/yacht-crew-jobs-no-experience", statusCode: 301 },
  // Ski instructor.
  { source: "/blog/how-to-become-ski-instructor", destination: "/guides/ski-instructor-jobs", statusCode: 301 },
  // Visas.
  { source: "/blog/working-in-europe-after-brexit-visa-guide", destination: "/guides/seasonal-work-visa-guide", statusCode: 301 },
];

/** Blog slugs retired by CONSOLIDATION_REDIRECTS; lib/blog.ts must not list them. */
export const CONSOLIDATED_BLOG_SLUGS: readonly string[] = CONSOLIDATION_REDIRECTS
  .map((r) => /^\/blog\/([^/]+)$/u.exec(r.source)?.[1])
  .filter((slug): slug is string => Boolean(slug));

// Destination guide slugs that need redirects from /ski/[slug] -> /guides/[slug]
const SKI_GUIDE_SLUGS = [
  "chamonix", "verbier", "meribel", "courchevel", "val-disere",
  "tignes", "morzine", "les-gets", "st-anton", "niseko",
  "whistler", "zermatt", "chalet-host-jobs", "ski-instructor-jobs",
  "transfer-driver-jobs", "what-to-wear-skiing",
];

const SUMMER_GUIDE_SLUGS = [
  "ibiza", "corfu", "mallorca", "mykonos", "sardinia", "palma",
  "antibes", "montenegro", "croatia", "lake-garda", "tenerife",
  "fort-lauderdale", "caribbean", "surf-instructor-jobs",
  "water-sports-instructor-jobs", "yacht-crew-jobs-no-experience",
  "yacht-stewardess-jobs", "greece-summer-jobs",
  "portugal-summer-jobs", "summer-camp-jobs-europe",
];

const GUIDE_REDIRECTS: Redirect[] = [
  ...SKI_GUIDE_SLUGS.map((slug): Redirect => ({
    source: `/ski/${slug}`,
    destination: `/guides/${slug}`,
    statusCode: 301,
  })),
  ...SUMMER_GUIDE_SLUGS.map((slug): Redirect => ({
    source: `/summer/${slug}`,
    destination: `/guides/${slug}`,
    statusCode: 301,
  })),
];

export const ALL_REDIRECTS: Redirect[] = [
  ...CONSOLIDATION_REDIRECTS,
  ...REDIRECTS,
  ...GUIDE_REDIRECTS,
  ...CANONICAL_SPOT_REDIRECTS,
];

export type ResolvedAppRedirect = Readonly<{
  location: string;
  statusCode: 301 | 308;
}>;

function escapePattern(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");
}

function matchRedirectSource(
  source: string,
  pathname: string,
): Record<string, string> | null {
  const names: string[] = [];
  const segments = source.split("/").slice(1);
  let pattern = "^";
  for (const segment of segments) {
    const catchAll = /^:([A-Za-z0-9_]+)\*$/u.exec(segment);
    if (catchAll) {
      names.push(catchAll[1]);
      pattern += "(?:/(.*))?";
      continue;
    }
    const parameter = /^:([A-Za-z0-9_]+)$/u.exec(segment);
    if (parameter) {
      names.push(parameter[1]);
      pattern += "/([^/]+)";
      continue;
    }
    pattern += `/${escapePattern(segment)}`;
  }
  const match = new RegExp(`${pattern}/?$`, "u").exec(pathname);
  if (!match) return null;
  return Object.fromEntries(names.map((name, index) => [
    name,
    match[index + 1] ?? "",
  ]));
}

function applyRedirectParameters(
  destination: string,
  parameters: Readonly<Record<string, string>>,
): string {
  return destination.replace(/:([A-Za-z0-9_]+)(\*)?/gu, (_match, name: string) =>
    parameters[name] ?? ""
  );
}

function resolveSingleRedirect(
  pathname: string,
  search: string,
): Readonly<{ url: URL; statusCode: 301 | 308 }> | null {
  for (const redirect of ALL_REDIRECTS) {
    const parameters = matchRedirectSource(redirect.source, pathname);
    if (!parameters) continue;
    const substituted = applyRedirectParameters(
      redirect.destination,
      parameters,
    );
    const destination = new URL(substituted, "https://peakwave.invalid");
    const original = new URLSearchParams(search.startsWith("?")
      ? search.slice(1)
      : search);
    for (const [name, value] of destination.searchParams) {
      original.set(name, value);
    }
    destination.search = original.toString();
    return { url: destination, statusCode: redirect.statusCode };
  }
  return null;
}

// Legacy URLs can match more than one entry (for example a /summer alias of
// a bare sailing slug). Follow the table to its final destination so crawlers
// and people get one hop. The status of the first hop is kept.
const MAX_REDIRECT_HOPS = 5;

/**
 * Retired Today and weekly URLs (forecast areas spec, section 10.5). Checked before the table:
 * one 301 straight to a board or an area page, with the original query dropped rather than
 * merged. `/api/today/*` is not matched and 404s.
 */
export function resolveRetiredTodayRedirect(
  pathname: string,
  search = "",
): ResolvedAppRedirect | null {
  const location = retiredTodayDestination(pathname, search);
  return location ? { location, statusCode: 301 } : null;
}

export function resolveAppRedirect(
  pathname: string,
  search = "",
): ResolvedAppRedirect | null {
  const retired = resolveRetiredTodayRedirect(pathname, search);
  if (retired) return retired;
  const first = resolveSingleRedirect(pathname, search);
  if (!first) return null;
  let current = first.url;
  const visited = new Set([pathname]);
  for (let hop = 1; hop < MAX_REDIRECT_HOPS; hop += 1) {
    if (visited.has(current.pathname)) break;
    visited.add(current.pathname);
    const next = resolveSingleRedirect(current.pathname, current.search);
    if (!next) break;
    if (!next.url.hash) next.url.hash = current.hash;
    current = next.url;
  }
  return {
    location: `${current.pathname}${current.search}${current.hash}`,
    statusCode: first.statusCode,
  };
}
